import React, { useEffect, useState } from "react";
import {
  Box,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";

import CustomTable from "src/components/Table/Table";
import { useDispatch, useSelector } from "react-redux";

import {
  getParentsListAction,
  updateParentStatusAction,
  deleteParentAction,
} from "src/api/parents";

import CustomBackdrop from "src/components/CustomBackdrop/CustomBackdrop";
import { showToast } from "src/customHooks/ToastEmitter";

export default function Parents() {
  const dispatch = useDispatch();

  const parentsList = useSelector(
    (state: any) => state.parents?.parentsList
  );

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    pageNo: 1,
    pageSize: 10,
  });

  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [addresses, setAddresses] = useState<any[]>([]);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const fetchParents = async (payload: any) => {
    try {
      setLoading(true);
      await dispatch(getParentsListAction(payload));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParents(formData);
  }, []);

  const fetchData = (data: any) => {
    const payload = {
      ...formData,
      pageNo: data.pageNumber,
      pageSize: data.pageSize,
    };
    setFormData(payload);
    fetchParents(payload);
  };

  const handleStatusChange = async (row: any, status: any) => {
    try {
      setLoading(true);
      await dispatch(updateParentStatusAction({ id: row.id, status }));
      showToast("Status updated", { appearance: "success" });
      fetchParents(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (row: any) => {
    setSelectedRow(row);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await dispatch(deleteParentAction(selectedRow.id));
      showToast("Deleted successfully", { appearance: "success" });
      fetchParents(formData);
    } finally {
      setDeleteOpen(false);
      setSelectedRow(null);
      setLoading(false);
    }
  };

  const openAddressModal = (addr: any[]) => {
    setAddresses(addr || []);
    setOpenAddressDialog(true);
  };

  // ✅ UPDATED COLUMNS
  const columns = [
    { field: "profile", headerName: "", width: 80 },
    { field: "name", headerName: "NAME", width: 180 },
    { field: "email", headerName: "EMAIL", width: 240 },
    { field: "phone", headerName: "PHONE", width: 160 },
    { field: "childCount", headerName: "CHILD COUNT", width: 140 }, // 👈 NEW
    { field: "status", headerName: "STATUS", width: 150 },
    { field: "action", headerName: "ACTION", width: 100 },
    { field: "address", headerName: "", width: 140 },
  ];

  const childColumns = [
    { field: "profile", headerName: "" },
    { field: "name", headerName: "NAME" },
    { field: "dob", headerName: "DOB" },
    { field: "grade", headerName: "GRADE" },
    { field: "school", headerName: "SCHOOL" },
    { field: "emergencyContact", headerName: "EMERGENCY CONTACT" },
    { field: "emergencyPhone", headerName: "EMERGENCY PHONE" },
  ];

  // ✅ UPDATED ACTION CONTENT
  const actionContent = (column: any, row: any) => {
    if (column === "profile") {
      return (
        <Box display="flex" justifyContent="center">
          <Avatar>{row.name?.charAt(0)}</Avatar>
        </Box>
      );
    }

    // 👇 NEW CHILD COUNT LOGIC
    if (column === "childCount") {
      return (
        <Typography sx={{ textAlign: "center", fontWeight: 500 }}>
          {row.children?.length || 0}
        </Typography>
      );
    }

    if (column === "status") {
      return (
        <Box display="flex" justifyContent="center">
          <FormControl size="small">
            <Select
              value={row.status}
              onChange={(e) =>
                handleStatusChange(row, e.target.value)
              }
              sx={{
                backgroundColor: row.status ? "#dcfce7" : "#fee2e2",
                color: row.status ? "green" : "red",
                height: "32px",
                borderRadius: "6px",
              }}
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      );
    }

    if (column === "action") {
      return (
        <Box display="flex" justifyContent="center">
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDeleteClick(row)}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    if (column === "address") {
      return (
        <Box display="flex" justifyContent="center">
          <Typography
            sx={{
              textDecoration: "underline",
              cursor: "pointer",
              color: "#1976d2",
              fontWeight: 500,
            }}
            onClick={() => openAddressModal(row.addresses)}
          >
            Show Addresses
          </Typography>
        </Box>
      );
    }

    return (
      <Typography sx={{ textAlign: "center" }}>
        {row[column]}
      </Typography>
    );
  };

  return (
    <>
      {loading && <CustomBackdrop open={loading} />}

      <Box mt={3} sx={{ pt: 1 }}>
        <CustomTable
          rows={parentsList?.data || []}
          columns={columns}
          total={parentsList?.total || 0}
          expandable
          expandColumns={childColumns}
          expandDataKey="children"
          action={actionContent}
          fetchData={fetchData}
          resetPagination={() => {}}
        />
      </Box>

      {/* ADDRESS DIALOG */}
      <Dialog
        open={openAddressDialog}
        onClose={() => setOpenAddressDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          Saved Addresses

          <IconButton onClick={() => setOpenAddressDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {addresses.length === 0 && (
            <Typography>No addresses found</Typography>
          )}

          <Box>
            {addresses.map((a, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  gap: 2,
                  p: 2,
                  mb: 2,
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                  boxShadow: "0px 3px 10px rgba(0,0,0,0.08)",
                  border: "1px solid #eee",
                  maxWidth: "420px",
                }}
              >
                <Box mt={0.5}>
                  {a.type === "Home" ? (
                    <HomeIcon color="primary" />
                  ) : (
                    <BusinessIcon color="primary" />
                  )}
                </Box>

                <Box flex={1}>
                  <Typography fontWeight={600}>{a.type}</Typography>

                  <Typography fontSize={14} color="#555">
                    {a.address}
                  </Typography>

                  <Typography fontSize={13} color="#888">
                    {a.landmark} • {a.pincode}
                  </Typography>

                  <Box mt={1.5}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LocationOnIcon />}
                      sx={{ textTransform: "none" }}
                      onClick={() =>
                        window.open(
                          `https://www.google.com/maps?q=${a.lat},${a.lng}`,
                          "_blank"
                        )
                      }
                    >
                      View on map
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Confirmation</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{selectedRow?.name}</strong>?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>

          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}